import { supabase } from '@/integrations/supabase/client';
import { resizeImage } from '@/lib/imageResizeUtils';
import { STORAGE_BUCKETS } from '@/lib/storageBuckets';
import type { UserItem } from '@/types/userItem';
import type { MatchedItem } from '@/components/inventory/useBulkMatchQueue';

export type BulkPhotoBatchType = 'singles' | 'slabs';

export interface BulkUploadResult {
  mode: 'matched' | 'draft';
  item?: UserItem;
}

export interface BulkUploadPairData {
  pairKey: string;
  files: File[];
  resolution: { mode: 'matched'; item: MatchedItem } | { mode: 'draft' };
  batchType: BulkPhotoBatchType;
  /**
   * Fired as soon as the draft row is inserted (before any photo bytes are
   * uploaded). Lets the caller hand the item off to downstream flows without
   * waiting for the full photo pipeline to settle.
   */
  onDraftCreated?: (item: UserItem) => void;
}


/**
 * Serialize attachments to the same inventory item so concurrent uploads don't
 * race on `display_order`. This is an in-memory guard for a single browser
 * session; a DB unique constraint on `(item_id, display_order)` would close it
 * across sessions.
 */
const itemLocks = new Map<string, Promise<unknown>>();

async function withItemLock<T>(itemId: string, fn: () => Promise<T>): Promise<T> {
  const previous = itemLocks.get(itemId) ?? Promise.resolve();
  const next = previous.then(fn, fn);
  itemLocks.set(itemId, next);
  try {
    return await next;
  } finally {
    if (itemLocks.get(itemId) === next) {
      itemLocks.delete(itemId);
    }
  }
}

/**
 * Upload a front/back pair and either attach it to an existing item or create a
 * new private draft. This is the unit of work passed to the upload queue.
 */
export async function processBulkUploadPair(
  data: BulkUploadPairData,
  signal: AbortSignal,
): Promise<BulkUploadResult> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Please sign in to upload');
  if (signal.aborted) throw new Error('Upload cancelled');

  const { files, resolution, batchType, onDraftCreated } = data;

  if (resolution.mode === 'matched') {
    await withItemLock(resolution.item.id, () =>
      attachPhotosToItem(user.id, resolution.item, files, signal)
    );
    return { mode: 'matched', item: resolution.item as unknown as UserItem };
  }

  const item = await createDraftWithPhotos(user.id, batchType, files, signal, onDraftCreated);
  return { mode: 'draft', item };
}


async function createDraftWithPhotos(
  userId: string,
  batchType: BulkPhotoBatchType,
  files: File[],
  signal: AbortSignal,
  onDraftCreated?: (item: UserItem) => void,
): Promise<UserItem> {
  const { data: item, error: itemErr } = await supabase
    .from('user_items')
    .insert({
      user_id: userId,
      title: '',
      category: batchType,
      condition: 'near_mint',
      visibility: 'private',
      is_listed: false,
      quantity: 1,
    } as never)
    .select()
    .single();
  if (itemErr || !item) throw itemErr ?? new Error('insert failed');
  if (signal.aborted) throw new Error('Upload cancelled');

  // Notify caller immediately so downstream flows (e.g. the pricing queue) can
  // start without waiting on the photo bytes.
  try { onDraftCreated?.({ ...(item as object), images: [] } as unknown as UserItem); } catch { /* noop */ }


  const itemId = (item as { id: string }).id;
  const uploadedImages: { image_url: string; display_order: number }[] = [];

  for (let j = 0; j < files.length; j++) {
    if (signal.aborted) throw new Error('Upload cancelled');
    const resized = await resizeImage(files[j]);
    const fileName = `${userId}/${itemId}/${Date.now()}-${j}.jpg`;
    const { error: upErr } = await supabase.storage
      .from(STORAGE_BUCKETS.listingImages)
      .upload(fileName, resized, { contentType: resized.type, cacheControl: '31536000' });
    if (upErr) throw upErr;
    const { data: { publicUrl } } = supabase.storage.from(STORAGE_BUCKETS.listingImages).getPublicUrl(fileName);
    const { error: rowErr } = await supabase.from('user_item_images').insert({
      item_id: itemId,
      image_url: publicUrl,
      display_order: j,
    });
    if (rowErr) throw rowErr;
    uploadedImages.push({ image_url: publicUrl, display_order: j });
  }

  return { ...(item as object), images: uploadedImages } as unknown as UserItem;
}

async function attachPhotosToItem(
  userId: string,
  item: MatchedItem,
  files: File[],
  signal: AbortSignal,
): Promise<void> {
  const { data: existing, error: readErr } = await supabase
    .from('user_item_images')
    .select('display_order')
    .eq('item_id', item.id);
  if (readErr) throw readErr;

  const maxOrder = (existing ?? []).reduce(
    (max, row) => Math.max(max, (row as { display_order: number | null }).display_order ?? 0),
    -1
  );

  for (let j = 0; j < files.length; j++) {
    if (signal.aborted) throw new Error('Upload cancelled');
    const resized = await resizeImage(files[j]);
    const fileName = `${userId}/${item.id}/${Date.now()}-${j}.jpg`;
    const { error: upErr } = await supabase.storage
      .from(STORAGE_BUCKETS.listingImages)
      .upload(fileName, resized, { contentType: resized.type, cacheControl: '31536000' });
    if (upErr) throw upErr;
    const { data: { publicUrl } } = supabase.storage.from(STORAGE_BUCKETS.listingImages).getPublicUrl(fileName);
    const { error: rowErr } = await supabase.from('user_item_images').insert({
      item_id: item.id,
      image_url: publicUrl,
      display_order: maxOrder + 1 + j,
    });
    if (rowErr) throw rowErr;
  }

  if (item.stock_image_url) {
    const { error: clearErr } = await supabase
      .from('user_items')
      .update({ stock_image_url: null })
      .eq('id', item.id)
      .eq('user_id', userId);
    if (clearErr) console.warn('failed to clear stock_image_url', clearErr);
  }
}
